import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Briefcase, Building2, MapPin, Clock, CheckCircle,
  XCircle, AlertCircle, ChevronRight, Loader2, FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyApplicationsPage = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const token = localStorage.getItem('codearena_token');

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const res = await axios.get(`${API}/codearena/my-applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(res.data.applications || []);
    } catch (err) {
      console.error('Failed to fetch applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'submitted':
        return <FileText className="text-blue-400" size={20} />;
      case 'assessment':
        return <AlertCircle className="text-yellow-400" size={20} />;
      case 'shortlisted':
        return <CheckCircle className="text-purple-400" size={20} />;
      case 'selected':
        return <CheckCircle className="text-green-400" size={20} />;
      case 'rejected':
        return <XCircle className="text-red-400" size={20} />;
      default:
        return <Clock className="text-gray-400" size={20} />;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      submitted: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      assessment: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      shortlisted: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      selected: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return colors[status] || colors.submitted;
  };

  // Status steps for progress tracker
  const statusSteps = [
    { key: 'submitted', label: 'Submitted' },
    { key: 'assessment', label: 'Assessment' },
    { key: 'shortlisted', label: 'Shortlisted' },
    { key: 'selected', label: 'Selected' }
  ];

  const getStepIndex = (status) => {
    if (status === 'rejected') return -1;
    return statusSteps.findIndex(s => s.key === status);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#030303] text-white flex items-center justify-center">
        <div className="text-center">
          <Briefcase className="mx-auto text-gray-600 mb-4" size={48} />
          <h2 className="text-xl font-bold text-white mb-2">Login Required</h2>
          <p className="text-gray-400 mb-4">Please login to view your applications</p>
          <Link
            to="/codearena/auth"
            className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-medium"
          >
            Login to EvalentX Arena
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] text-white py-12">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">My Applications</h1>
            <p className="text-gray-400">Track your job application progress</p>
          </div>
          <Link
            to="/codearena/jobs"
            className="flex items-center gap-2 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30"
          >
            Browse Jobs
            <ChevronRight size={18} />
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-cyan-400" size={32} />
          </div>
        ) : applications.length === 0 ? (
          <div className="text-center py-16 bg-white/5 rounded-2xl border border-white/10">
            <Briefcase className="mx-auto text-gray-600 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-white mb-2">No applications yet</h3>
            <p className="text-gray-400 mb-4">Start applying to jobs and track your progress here</p>
            <Link
              to="/codearena/jobs"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-medium"
            >
              <Briefcase size={18} />
              Browse Jobs
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {applications.map(app => (
              <div
                key={app.id}
                className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-colors"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-xl flex items-center justify-center text-white font-bold">
                      {app.company_name?.charAt(0) || 'C'}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{app.job_title}</h3>
                      <div className="flex items-center gap-3 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <Building2 size={14} />
                          {app.company_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          Applied {new Date(app.applied_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${getStatusColor(app.status)}`}>
                    {getStatusIcon(app.status)}
                    <span className="font-medium capitalize">{app.status_info?.label || app.status}</span>
                  </div>
                </div>

                {/* Progress Tracker */}
                {app.status !== 'rejected' && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between relative">
                      {/* Progress Line */}
                      <div className="absolute left-0 right-0 top-1/2 h-1 bg-white/10 -translate-y-1/2 z-0" />
                      <div 
                        className="absolute left-0 top-1/2 h-1 bg-gradient-to-r from-cyan-500 to-purple-500 -translate-y-1/2 z-0 transition-all"
                        style={{ 
                          width: `${Math.max(0, (getStepIndex(app.status) / (statusSteps.length - 1)) * 100)}%` 
                        }}
                      />
                      
                      {statusSteps.map((step, idx) => {
                        const currentIdx = getStepIndex(app.status);
                        const isCompleted = idx <= currentIdx;
                        const isCurrent = idx === currentIdx;
                        
                        return (
                          <div key={step.key} className="relative z-10 flex flex-col items-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              isCompleted 
                                ? 'bg-gradient-to-r from-cyan-500 to-purple-500' 
                                : 'bg-white/10'
                            } ${isCurrent ? 'ring-4 ring-cyan-500/30' : ''}`}>
                              {isCompleted ? (
                                <CheckCircle className="text-white" size={16} />
                              ) : (
                                <span className="text-gray-500 text-xs">{idx + 1}</span>
                              )}
                            </div>
                            <span className={`text-xs mt-2 ${
                              isCompleted ? 'text-white' : 'text-gray-500'
                            }`}>
                              {step.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Rejected Status */}
                {app.status === 'rejected' && (
                  <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-red-400 text-sm">
                      Unfortunately, your application was not selected. Don't give up - keep applying!
                    </p>
                  </div>
                )}

                {/* Status History */}
                {app.status_history?.length > 1 && (
                  <div className="mt-4 pt-4 border-t border-white/5">
                    <p className="text-xs text-gray-500 mb-2">Status History</p>
                    <div className="flex flex-wrap gap-2">
                      {app.status_history.map((history, idx) => (
                        <span key={idx} className="text-xs text-gray-400">
                          {history.status} - {new Date(history.timestamp).toLocaleDateString()}
                          {idx < app.status_history.length - 1 && ' →'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyApplicationsPage;
